import React, {FC, useEffect, useMemo} from 'react';
import pick from 'lodash.pick';
import {getLocale} from '@epic-core/common/dist';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {Datapoint} from '@epic-visualizations/charts';
import {useComparisonColor, useVisualizationsTheme} from '@epic-visualizations/theme';

import Legends from './legends/Legends';
import useWidth from './useWidth';
import CustomTooltip from './CustomTooltip';
import {getCompactXAxis} from './compact-axis/CustomXAxis';
import {getCompactYAxis} from './compact-axis/CustomYAxis';
import {ChartSkeleton} from '../../../../../common/skeleton';

import {getColor} from './utils';
import useInteractiveLegends from './legends/useInteractiveLegends';

import {KeysMap, Trends} from './types';
import {Option, Formatter} from '../../../../../../types';

import {ChartContainer, StyledLineChart} from './styles';

export interface ChartProps {
    data: Datapoint[];
    height?: number;
    tooltipLabelFormatter?: Formatter<string | number>;
    xTickFormatter?: Formatter<string | number>;
    yTickFormatter?: Formatter<string | number>;
    tooltipFormatter?: any;
    selectedGames: Option[];
    xAxis?: string;
    trends?: Trends;
    limit?: number;
    loading: boolean;
    keysMap?: KeysMap;
    emptyDataKeys?: string[];
    yAxisProps?: object;
    xAxisProps?: object;
}

const defaultFormatter: Formatter<undefined> = () => undefined;

const LineChart: FC<ChartProps> = ({
    data: propsData,
    height,
    tooltipLabelFormatter = defaultFormatter,
    xTickFormatter = defaultFormatter,
    yTickFormatter = defaultFormatter,
    tooltipFormatter = defaultFormatter,
    trends,
    limit = 10,
    xAxis = 'time',
    loading,
    selectedGames: selectedGamesProps,
    keysMap = {},
    emptyDataKeys = [],
    yAxisProps,
    xAxisProps,
    ...props
}: ChartProps) => {
    const getComparisonColor = useComparisonColor();
    const visualizationsTheme = useVisualizationsTheme();
    const theme = useTheme();

    const isFmdBreakpoint = !useMediaQuery(theme.breakpoints.up('fmd'));
    const [containerRef, width] = useWidth();

    const tooltipProps = {
        isAnimationActive: true,
        allowEscapeViewBox: {x: false, y: false}
    };

    const selectedGames = selectedGamesProps.sort(
        ({order: a = 0}: Option, {order: b = 0}: Option) => Number(a) - Number(b)
    );

    // Used to display legends list
    const dataKeys = useMemo(() => selectedGames.map(({value}) => String(value)), [selectedGames]);

    const {
        hoveredKey,
        hiddenKeys,
        setHiddenKeys,
        onLegendClick,
        onLegendMouseEnter,
        onLegendMouseLeave
    } = useInteractiveLegends();

    useEffect(() => {
        setHiddenKeys(
            Array.from(new Set([...dataKeys.slice(limit, dataKeys.length), ...emptyDataKeys]))
        );
    }, [dataKeys, emptyDataKeys]);

    // Override the default LineChart colors for hover effect
    const dataColors = useMemo(
        () =>
            dataKeys.reduce(
                (acc, key, index) => ({
                    ...acc,
                    [key]: getColor(
                        getComparisonColor(index),
                        hoveredKey && !hiddenKeys.includes(hoveredKey) && hoveredKey !== key
                    )
                }),
                {}
            ),
        [getComparisonColor, hoveredKey, dataKeys]
    );

    const data = useMemo(() => {
        const keysToShow = [xAxis, ...dataKeys.filter(key => !hiddenKeys.includes(key))];

        return propsData.map(item => pick(item, keysToShow));
    }, [propsData, hiddenKeys, dataKeys]);

    if (loading) {
        return <ChartSkeleton pb={2} pt={4} height={520} width="100%" turnOver />;
    }

    const locale = getLocale();

    return (
        <>
            <ChartContainer ref={containerRef}>
                <StyledLineChart
                    responsive
                    height={height}
                    data={data}
                    locales={locale}
                    showLegend={false}
                    dataColors={dataColors}
                    xTickFormatter={xTickFormatter(locale)}
                    yTickFormatter={yTickFormatter(locale)}
                    tooltipLabelFormatter={tooltipLabelFormatter(locale)}
                    margin={{top: 5, right: 0, bottom: 15, left: 0}}
                    xAxisProps={getCompactXAxis({
                        visualizationsTheme,
                        theme,
                        width,
                        dataLength: data.length,
                        xAxisProps,
                        xTickFormatter: xTickFormatter(locale)
                    })}
                    yAxisProps={
                        isFmdBreakpoint
                            ? getCompactYAxis({
                                  visualizationsTheme,
                                  yAxisProps,
                                  yTickFormatter: yTickFormatter(locale)
                              })
                            : yAxisProps
                    }
                    tooltipProps={tooltipProps}
                    tooltipContent={CustomTooltip({
                        trends,
                        keysMap,
                        tooltipFormatter: tooltipFormatter(locale)
                    })}
                    {...props}
                />
            </ChartContainer>
            <Legends
                keysMap={keysMap}
                limit={limit}
                hoveredKey={hoveredKey}
                hiddenKeys={hiddenKeys}
                dataKeys={dataKeys}
                emptyDataKeys={emptyDataKeys}
                selectedGames={selectedGames}
                onClick={onLegendClick}
                onMouseEnter={onLegendMouseEnter}
                onMouseLeave={onLegendMouseLeave}
            />
        </>
    );
};

export default LineChart;
